import { memo, useCallback, useEffect, useRef } from "react";
import { BzMasterAgGrid } from "../../components";
import { Container } from "react-bootstrap";
import { displayZeroStateForGrid, getApiExchangeNameFromMktSegId, getAssetExchangeNameFromMktSegId, getScripDisplayDescription } from "../../utils/global_functions";
import { getPeerComparisonData } from "../../redux/actions/marketOverview";
import { useSelector, useDispatch } from "react-redux";
import { WATCHLIST_GRID_INDEX } from "../../utils/global_variables";
import ZeroStates from "../ZeroStates";
import GridHeaderSearch from "../GridHeaderSearch";


const PeerComparison = () => {
  const peerComparison = useSelector(state => state.marketOverview.peerComparison);
  const scripOverviewData = useSelector((state) => state.marketOverview.scripOverviewData);
  const dispatch = useDispatch();
  let strSearch = null;
  const gridRef = useRef();
  const filterOptions = [
    'equals',
    'notEqual',
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank'
  ];

  const getFilterDataForGrid = ({ filterOption, value, filterText }) => {
    if (filterText == null) {
      return false;
    } else {
      switch (filterOption) {
        case 'equals': {
          return parseFloat(filterText) === parseFloat(value);
        }
        case 'notEqual': {
          return parseFloat(filterText) !== parseFloat(value);
        }
        case 'lessThan': {
          return parseFloat(value) < parseFloat(filterText);
        }
        case 'lessThanOrEqual': {
          return parseFloat(value) <= parseFloat(filterText);
        }
        case 'greaterThan': {
          return parseFloat(value) > parseFloat(filterText);
        }
        case 'greaterThanOrEqual': {
          return parseFloat(value) >= parseFloat(filterText);
        }
        case 'inRange': {
          const agFilterModel = gridRef?.current?.api?.getFilterModel();
          if (Object?.keys(agFilterModel)?.length > 0) {
            const fromValue = agFilterModel[Object?.keys(agFilterModel)[0]].filter;
            const toValue = agFilterModel[Object?.keys(agFilterModel)[0]].filterTo;
            return parseFloat(value) >= parseFloat(fromValue) && parseFloat(value) <= parseFloat(toValue);
          } else {
            return false;
          }
        }
        case 'blank': {
          return false;
        }
        case 'notBlank': {
          return true;
        }
        default:
          return false;
      }
    }
  };

  const sortingComparator = useCallback((number1, number2) => {
    number1 = parseFloat(number1);
    number2 = parseFloat(number2);
    if (number1 === null) {
      return 0.00;
    }
    if (number2 === null) {
      return 0.00;
    }
    return number1 - number2;
  }, []);
  useEffect(() => {
    if (peerComparison?.length === 0) {
      const exchangeType = getApiExchangeNameFromMktSegId(scripOverviewData[WATCHLIST_GRID_INDEX.SEGMENT_ID]);
      const objPayload = {
        // BT-114569 : Functional Implementation : Vaibhav Singh : 01-07-2023 : fixed for apicall when inline search is triggered
        nToken: scripOverviewData[WATCHLIST_GRID_INDEX.TOKEN],
        exchangeType: exchangeType,
        mktSegmentId: getAssetExchangeNameFromMktSegId(scripOverviewData[WATCHLIST_GRID_INDEX.SEGMENT_ID]),
        jtenantToken: 1,
      };
      dispatch(getPeerComparisonData(objPayload));
    }
    // Vaibhav Singh: BT-109095: 09-11-2023:PeerComparison tofixed added and column filter removed: Start 
    else {
      peerComparison?.forEach((item) => {
        Object.keys(item)?.forEach((e) => {
          // Vaibhav Singh: BT-109095: 23-11-2023: fixed NAN issue: Start 
          if (e !== 'Symbol') {
            if (item[e] !== '-') {
              item[e] = parseFloat(item[e]).toFixed(2);
            }
          }
          // Vaibhav Singh: BT-109095: 23-11-2023: fixed NAN issue: End
        });
      });
    }
  }, [scripOverviewData, peerComparison, dispatch]);
  const onChangeGridHeaderSearch = useCallback((e) => {
    strSearch = e?.target?.value;
    gridRef?.current?.api?.onFilterChanged();
    displayZeroStateForGrid(gridRef);
  }, []);
  const isExternalFilterPresent = useCallback(() => {
    return strSearch !== null;
  }, [strSearch]);

  const doesExternalFilterPass = useCallback(
    (node) => {
      if (!strSearch) {
        return true;
      } else {
        //Vaibhav Singh: BT-116978 : Fixed filter for row data in search
        //Azeem k :optamize search functionality 26-10-2023
        const keywords = strSearch?.toString()?.toLowerCase()?.split(' ');
        let strRowData = node?.data?.Symbol;
        let bIsMatch = keywords?.every(keyword => strRowData?.toLowerCase()?.includes(keyword));
        setTimeout(() => {
          const arrFiltered = gridRef?.current?.api?.getModel()?.rowsToDisplay?.filter((row) => {
            return row['displayed'] === true;
          });
          if (arrFiltered?.length > 0) {
            strSearch = null;
          } else {
            strSearch = null;
          }
        }, 10);
        return bIsMatch;
      }
    },
    [strSearch]
  );
  const columnDefs = [
    {
      headerName: "Company",
      field: 'Symbol',
      cellClass: 'text-start',
      pinned: 'left',
      suppressMovable: true,
      suppressNavigable: true,
      lockPosition: "left",
      headerComponent: GridHeaderSearch,
      headerComponentParams: { onChangeGridHeaderSearch, gridRef: gridRef },
      // filterParams: {
      //   textMatcher: getFilterDataForGrid,
      //   filterOptions: filterOptions
      // },
      width: 200,

      suppressMovable: true,
      lockPosition: "left",
      suppressNavigable: true,
    },
    {
      headerName: "Price (LTP)",
      field: 'LTP',
      width: 140,
      cellClass: 'text-start',
      filter: 'agTextColumnFilter',
      pinned: 'left',
      suppressMovable: true,
      suppressNavigable: true,
      lockPosition: "left",
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    },
    {
      headerName: '% Change',
      field: 'PerChange',
      width: 100,
      cellClassRules: {
        'color-positive': params => !params?.value?.includes('-'),
        'color-negative': params => params?.value?.includes('-'),
      },
      cellClass: 'text-center',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? !params?.value.includes('-') ? `+${parseFloat(params?.value).toFixed(2)}` : parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    },
    {
      headerName: 'MCAP (Cr.)',
      field: 'MCAP',
      width: 100,
      cellClass: 'text-center',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    },
    {
      headerName: 'PE (TTM)',
      field: 'PE_TTM',
      width: 100,
      cellClass: 'text-center',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    },
    {
      headerName: 'PBV (TTM)',
      field: 'PBV_TTM',
      width: 100,
      cellClass: 'text-center',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    },
    {
      headerName: '1 Yr. return',
      field: 'Ret1Year',
      width: 100,
      cellClass: 'text-center',
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: getFilterDataForGrid,
        filterOptions: filterOptions
      },
      valueFormatter: params => { return params?.value && params?.value !== '-' ? parseFloat(params?.value).toFixed(2) : '-'; },
      comparator: sortingComparator,
    }
  ];

  const onExportMarketMovement = useCallback(() => {
    if (peerComparison?.length) {
      gridRef?.current?.api?.exportDataAsExcel({ fileName: `${scripOverviewData?.[WATCHLIST_GRID_INDEX.SYMBOL]}_${scripOverviewData[WATCHLIST_GRID_INDEX.EXCHANGE]}_${getScripDisplayDescription(scripOverviewData)?.split(" ")?.join("")}_PeerComparison.xlsx` });
    }
  }, [peerComparison, scripOverviewData]);


  

  return (
    <>
      <Container data-testid="market-overview-peer-comparison-section" fluid className='p-0 mt-3'>
        <div className='scripoverview-fieldset'>
          <div className='scripoverview-fieldset-sm'>
            <div className='scripoverview-legend'>PEER COMPARISON</div>
            <div className='mt-3'>
              {(peerComparison?.length) ?
                <div className='ag-theme-alpine'>
                  <BzMasterAgGrid
                    testId={"market-overview-peer-comparison-grid"}
                    isCompress={true}
                    ref={gridRef}
                    className={'so-analysis-peer-comparison'}
                    arrColumnData={columnDefs}
                    defaultColProps={{flex:1}}
                    isExternalFilterPresent={isExternalFilterPresent}
                    doesExternalFilterPass={doesExternalFilterPass}
                    arrRowData={peerComparison} />
                </div>
                : <div className='zero-state-alerts zero-state-alerts-lg'>
                  <ZeroStates subText="Could not find any data" icon='icon-64 icon-nodata mb-3 mrgauto' />
                </div>}
            </div>

            <div className='fixed-icons-wrapper'>
              <div className='d-flex justify-content-end align-items-center'>
                <div className='background-icon focus p-1' onClick={() => onExportMarketMovement()}>
                  <div data-testid="market-overview-peer-comparison-download-icon" className={`icon-16 icon-download ${peerComparison?.length ? 'icon-dark' : 'icon-grey'}`}></div>
                </div>
                {/* <div className='background-icon ms-3' onClick={() => onExportMarketMovement()}>
                  <div className={`icon-16 icon-kebab ${peerComparison?.length?'icon-dark':'icon-grey'}`}></div>
                </div>   */}
              </div>
            </div>

          </div>
        </div>

      </Container>
    </>

  );
};

export default memo(PeerComparison);


